import { supabase } from '@/lib/supabase';
import { logPaymentAttempt } from '@/lib/paymentAttempts';
import { logger } from '@/lib/logger';
import { getMerchantPaymentAccess } from '@/lib/merchantPaymentAccess';

// ─────────────────────────────────────────────────────────────
// platformPayments — configuration de paiement pilotée par le SuperAdmin.
//
// Le SuperAdmin choisit :
//   • les moyens actifs (table `payment_providers`) ;
//   • la portée (`platform_payment_settings.scope`) :
//       - « subscriptions » → uniquement les abonnements Zifek ;
//       - « all »           → les boutiques peuvent aussi encaisser ;
//   • la commission appliquée à chaque transaction.
//
// Ce module est la source de vérité côté client : les tunnels publics
// n'affichent le paiement en ligne QUE si la portée est « all » et qu'un
// moyen réellement branché (FeexPay) est activé. Le serveur revérifie la
// portée avant toute création de paiement : la règle n'est donc pas
// contournable depuis le navigateur.
// ─────────────────────────────────────────────────────────────

export interface PlatformFeeLine {
  label: string;
  percent: number;
}

export interface PlatformProvider {
  provider: string;
  label: string;
}

export interface PlatformPaymentConfig {
  scope: 'subscriptions' | 'all';
  commission: number;
  breakdown: PlatformFeeLine[];
  providers: PlatformProvider[];
  /** Tunnels réellement implémentés côté serveur. */
  onlineProviders: string[];
  /** Vrai si les boutiques peuvent encaisser en ligne. */
  shopOnlineEnabled: boolean;
}

const ONLINE_PROVIDERS = ['feexpay'];

export const DEFAULT_PLATFORM_PAYMENT_CONFIG: PlatformPaymentConfig = {
  scope: 'subscriptions',
  commission: 6,
  breakdown: [],
  providers: [],
  onlineProviders: ONLINE_PROVIDERS,
  shopOnlineEnabled: false,
};

export async function loadPlatformPaymentConfig(): Promise<PlatformPaymentConfig> {
  try {
    const [settingsRes, providersRes] = await Promise.all([
      supabase
        .from('platform_payment_settings')
        .select('scope, commission_percent, fee_breakdown')
        .eq('id', 1)
        .maybeSingle(),
      supabase
        .from('payment_providers')
        .select('provider, label, enabled')
        .eq('enabled', true)
        .order('sort_order'),
    ]);

    const row = settingsRes.data as {
      scope?: string | null;
      commission_percent?: number | null;
      fee_breakdown?: unknown;
    } | null;

    const providers = ((providersRes.data || []) as { provider: string; label: string }[])
      .map((p) => ({ provider: String(p.provider || ''), label: String(p.label || p.provider || '') }))
      .filter((p) => p.provider);

    const scope: 'subscriptions' | 'all' = row?.scope === 'all' ? 'all' : 'subscriptions';
    const breakdown = Array.isArray(row?.fee_breakdown)
      ? (row?.fee_breakdown as PlatformFeeLine[]).filter((l) => l && typeof l.label === 'string')
      : [];

    const onlineEnabled = providers.some((p) => ONLINE_PROVIDERS.includes(p.provider));

    return {
      scope,
      commission: Number(row?.commission_percent) || 6,
      breakdown,
      providers,
      onlineProviders: ONLINE_PROVIDERS,
      shopOnlineEnabled: scope === 'all' && onlineEnabled,
    };
  } catch {
    return DEFAULT_PLATFORM_PAYMENT_CONFIG;
  }
}

export interface ShopCheckoutResult {
  url: string | null;
  reference: string | null;
  status: string;
  amount?: number;
  gatewayAmount?: number;
  currency?: string;
  commissionPercent?: number;
  instructions?: string;
}

/**
 * Demande un paiement en ligne pour une commande de boutique.
 * Le serveur vérifie la portée, l'activation du moyen et enregistre la
 * transaction dans le tableau de bord des paiements.
 */
export async function createShopOnlinePayment(
  orderId: number,
  options: {
    network?: string;
    method?: 'mobile_money' | 'card';
    amount?: number;
    currency?: string;
    phone?: string;
    country?: string;
  } = {},
): Promise<ShopCheckoutResult> {
  const { data, error } = await supabase.functions.invoke('zifek-shop-checkout', {
    body: {
      orderId,
      network: options.network || '',
      method: options.method || 'mobile_money',
      phone: options.phone || '',
      country: options.country || '',
      origin: typeof window !== 'undefined' ? window.location.origin : '',
      basePath: typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : '',
    },
  });

  if (error) {
    let message = "Le paiement en ligne n'est pas disponible pour le moment.";
    let rawBody: unknown = null;
    try {
      const ctx = (error as { context?: { json?: () => Promise<{ error?: string }> } }).context;
      if (ctx && typeof ctx.json === 'function') {
        const body = await ctx.json();
        rawBody = body;
        if (body?.error) message = body.error;
      }
    } catch {
      /* message par défaut */
    }
    // Journal des paiements : on garde la trace de l'échec côté navigateur
    // (le serveur journalise déjà sa propre réponse brute).
    void logPaymentAttempt({
      provider: 'feexpay',
      flow: 'shop_order',
      orderId,
      amount: options.amount ?? null,
      currency: options.currency ?? null,
      operator: options.network ?? null,
      country: options.country ?? null,
      phone: options.phone ?? null,
      errorMessage: message,
      rawResponse: rawBody || (error as { message?: string })?.message,
      requestPayload: { orderId, method: options.method || 'mobile_money', network: options.network },
    });
    throw new Error(message);
  }

  const result = (data || { url: null, reference: null, status: 'pending' }) as ShopCheckoutResult;
  logger.info('paiement', 'Paiement boutique initié', {
    commande: orderId,
    operateur: options.network || null,
    montant: result.amount ?? options.amount ?? null,
    devise: result.currency ?? options.currency ?? null,
    reference: result.reference,
  });
  return result;
}

// ─────────────────────────────────────────────────────────────
// Moyens de paiement réellement proposés sur le checkout d'UNE boutique.
//
// Trois conditions cumulatives :
//   1. le SuperAdmin a activé le moyen au niveau plateforme ;
//   2. le commerce a l'autorisation d'encaisser (portée globale ou accès
//      nominatif dans `merchant_payment_access`) ;
//   3. le commerce a choisi ce moyen pour sa boutique.
// Le serveur revérifie les points 2 et 3 avant tout débit : ce module ne
// sert qu'à afficher la bonne interface.
// ─────────────────────────────────────────────────────────────

export interface MerchantCheckoutMethod {
  provider: string;
  label: string;
  /** Moyen réellement branché côté serveur (FeexPay aujourd'hui). */
  ready: boolean;
}

export interface MerchantCheckoutConfig {
  /** Le commerce peut-il encaisser en ligne ? */
  authorized: boolean;
  /** Un moyen utilisable est-il disponible ? */
  onlineEnabled: boolean;
  methods: MerchantCheckoutMethod[];
  commission: number;
}

export async function loadMerchantCheckoutConfig(commerceId: number): Promise<MerchantCheckoutConfig> {
  const config = await loadPlatformPaymentConfig();
  if (!commerceId) {
    return { authorized: false, onlineEnabled: false, methods: [], commission: config.commission };
  }

  const access = await getMerchantPaymentAccess(commerceId);
  const authorized = config.scope === 'all' || access?.enabled === true;
  const chosen = access?.methods && access.methods.length > 0 ? access.methods : null;

  const methods: MerchantCheckoutMethod[] = config.providers
    .filter((p) => (chosen ? chosen.includes(p.provider) : true))
    .map((p) => ({
      provider: p.provider,
      label: p.label || p.provider,
      ready: config.onlineProviders.includes(p.provider),
    }));

  return {
    authorized,
    onlineEnabled: authorized && methods.some((m) => m.ready),
    methods,
    commission: access?.commissionPercent ?? config.commission,
  };
}